"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.facebookAdsAdSetManagement = exports.facebookAdsAdSetManagementArgsSchema = exports.FacebookAdsAdSetAction = exports.facebookAdsUpdateAdSetResultSchema = exports.facebookAdsUpdateAdSetArgsSchema = exports.facebookAdsUpdateAdSetArgsSchemaBase = exports.facebookAdsCreateAdSetResultSchema = exports.facebookAdsCreateAdSetArgsSchema = exports.facebookAdsCreateAdSetArgsSchemaBase = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// ---------- Create Ad Set ----------
// Promoted object schema for different campaign objectives
var promotedObjectSchema = zod_1.z.union([
    zod_1.z.object({
        type: zod_1.z.literal('page'),
        page_id: zod_1.z
            .string()
            .nullable()
            .optional()
            .describe('Facebook page ID to promote. Uses selected page from Facebook integration credentials by default, with user-provided page_id as backup option.'),
    }),
    zod_1.z.object({
        type: zod_1.z.literal('pixel'),
        pixel_id: zod_1.z.string().describe('Facebook pixel ID for conversion tracking'),
        custom_event_type: zod_1.z
            .enum([
            'PURCHASE',
            'LEAD',
            'COMPLETE_REGISTRATION',
            'ADD_TO_CART',
            'PAGE_VIEW',
            'INITIATED_CHECKOUT',
        ])
            .nullable()
            .optional()
            .describe('Custom conversion event type (required when pixel_id is specified)'),
    }),
    zod_1.z.object({
        type: zod_1.z.literal('app'),
        application_id: zod_1.z
            .string()
            .describe('Facebook application ID for app install or app event campaigns'),
        object_store_url: zod_1.z
            .string()
            .describe('App store URL (iOS App Store or Google Play Store URL). Example: https://apps.apple.com/app/id123456789 or https://play.google.com/store/apps/details?id=com.example.app'),
        custom_event_type: zod_1.z
            .enum([
            'PURCHASE',
            'COMPLETE_REGISTRATION',
            'ADD_TO_CART',
            'INITIATED_CHECKOUT',
            'ADD_PAYMENT_INFO',
            'LEVEL_ACHIEVED',
            'RATE',
            'MOBILE_APP_INSTALL',
        ])
            .nullable()
            .optional()
            .describe('App event type for optimization (e.g., PURCHASE for in-app purchases, MOBILE_APP_INSTALL for app installs). Required for OUTCOME_SALES with app conversion location.'),
    }),
    zod_1.z.object({
        type: zod_1.z.literal('leadgen'),
        page_id: zod_1.z
            .string()
            .nullable()
            .optional()
            .describe('Facebook page ID for the lead form. Uses selected page from Facebook integration credentials by default.'),
        leadgen_form_id: zod_1.z
            .string()
            .describe('Facebook Lead Gen Form ID. This is the instant form created using facebookAdsLeadgenFormManagement tool.'),
    }),
    zod_1.z.object({
        type: zod_1.z.literal('product_catalog'),
        product_set_id: zod_1.z
            .string()
            .describe('Product set ID from your Facebook product catalog. Get this from Meta Business Suite → Catalogs → Product Sets. Required for catalog/dynamic product ads.'),
        product_catalog_id: zod_1.z
            .string()
            .nullable()
            .optional()
            .describe('Product catalog ID (optional). If not provided, Meta will use the product set ID to infer the catalog.'),
    }),
]);
exports.facebookAdsCreateAdSetArgsSchemaBase = zod_1.z.object({
    campaignId: zod_1.z.string().describe('Campaign ID where ad set will be created'),
    name: zod_1.z.string().describe('Ad set name'),
    status: zod_1.z
        .enum(['ACTIVE', 'PAUSED'])
        .default('PAUSED')
        .describe('Ad set status (default: PAUSED)'),
    campaignHasBudget: zod_1.z
        .boolean()
        .default(true)
        .describe('Indicates whether the campaign has its own budget set (CBO mode). When true, ad set budget parameters (dailyBudget/lifetimeBudget) will be discarded to prevent Meta API conflicts. When false, the campaign uses Ad Set Budget Optimization (ABO) and ad set budgets are required. This helps the AI agent make intentional budget decisions and prevents campaign-level vs ad set-level budget conflicts.'),
    dailyBudget: zod_1.z
        .number()
        .nullable()
        .optional()
        .describe('Daily budget in account currency (cents). Minimum $30.00 (3000 cents). REQUIRED: Either dailyBudget or lifetimeBudget must be provided - Meta API requirement for ad set creation.'),
    lifetimeBudget: zod_1.z
        .number()
        .nullable()
        .optional()
        .describe('Lifetime budget in account currency (cents). Minimum $30.00 (3000 cents). REQUIRED: Either dailyBudget or lifetimeBudget must be provided - Meta API requirement for ad set creation. CRITICAL: When using lifetimeBudget, you MUST also provide either endTime (Unix timestamp) or durationDays to specify when the campaign should end. Facebook requires end dates for all lifetime budget campaigns.'),
    bidAmount: zod_1.z
        .number()
        .min(3000, 'Bid amount must be at least 3000 cents ($30.00)')
        .nullable()
        .optional()
        .describe('Bid amount for manual bid cap strategy (cents). Minimum $30.00 (3000 cents). RECOMMENDED: Omit this field to use automatic bidding (Meta optimizes automatically for best results). Only use when you need strict cost control.'),
    bidStrategy: zod_1.z
        .enum([
        'LOWEST_COST_WITHOUT_CAP',
        'LOWEST_COST_WITH_BID_CAP',
        'COST_CAP',
        'LOWEST_COST_WITH_MIN_ROAS',
    ])
        .default('LOWEST_COST_WITHOUT_CAP')
        .describe('Bid strategy for the ad set (default: LOWEST_COST_WITHOUT_CAP for automatic bidding). LOWEST_COST_WITHOUT_CAP: automatic bidding (RECOMMENDED - Meta optimizes automatically), LOWEST_COST_WITH_BID_CAP: maximum bid per auction (requires bidAmount), COST_CAP: target average cost (requires bidAmount), LOWEST_COST_WITH_MIN_ROAS: minimum ROAS constraint (requires bidConstraints). Only use manual bid strategies when you need strict cost control.'),
    bidConstraints: zod_1.z
        .object({
        roas_average_floor: zod_1.z
            .number()
            .nullable()
            .optional()
            .describe('Minimum ROAS target scaled by 10,000. Required when optimization goal is VALUE. Examples: 1.5 ROAS = 15000, 2.0 ROAS = 20000, 3.0 ROAS = 30000. Meta API requires values multiplied by 10,000.'),
    })
        .nullable()
        .optional()
        .describe('Advanced ROAS constraints for sales campaigns. RECOMMENDED: Omit this field to use automatic bidding. Only use with optimizationGoal "VALUE" when you need specific return on ad spend targeting.'),
    billingEvent: zod_1.z
        .enum(['IMPRESSIONS', 'LINK_CLICKS', 'OFFER_CLAIMS', 'PAGE_LIKES', 'POST_ENGAGEMENT'])
        .default('IMPRESSIONS')
        .describe('Billing event'),
    optimizationGoal: zod_1.z
        .enum([
        'NONE',
        'APP_INSTALLS',
        'AD_RECALL_LIFT',
        'ENGAGED_USERS',
        'EVENT_RESPONSES',
        'IMPRESSIONS',
        'QUALITY_CALL',
        'MEANINGFUL_CALL_ATTEMPT',
        'LEAD_GENERATION',
        'QUALITY_LEAD',
        'LINK_CLICKS',
        'LANDING_PAGE_VIEWS',
        'VISIT_INSTAGRAM_PROFILE',
        'OFFSITE_CONVERSIONS',
        'PAGE_LIKES',
        'POST_ENGAGEMENT',
        'REACH',
        'VALUE',
        'THRUPLAY',
        'DERIVED_EVENTS',
        'APP_INSTALLS_AND_OFFSITE_CONVERSIONS',
        'CONVERSATIONS',
        'IN_APP_VALUE',
        'MESSAGING_PURCHASE_CONVERSION',
        'SUBSCRIBERS',
        'REMINDERS_SET',
        'PROFILE_VISIT',
        'PROFILE_AND_PAGE_ENGAGEMENT',
        'ADVERTISER_SILOED_VALUE',
        'AUTOMATIC_OBJECTIVE',
        'MESSAGING_APPOINTMENT_CONVERSION',
    ])
        .default('LINK_CLICKS')
        .describe('Optimization goal'),
    targeting: zod_1.z
        .object({
        geo_locations: zod_1.z
            .object({
            countries: zod_1.z
                .array(zod_1.z.string())
                .nullable()
                .optional()
                .describe('ISO 3166-1 alpha-2 country codes ONLY (e.g., ["US", "CA", "GB", "AU"]). Must be 2-letter codes, NOT country names. WRONG: ["United States", "Canada"]. CORRECT: ["US", "CA"]. At least one of countries, regions, or cities is required for ad set creation UNLESS savedAudienceId is provided (saved audience will provide targeting).'),
            regions: zod_1.z
                .array(zod_1.z.object({ key: zod_1.z.string() }))
                .nullable()
                .optional()
                .describe('Region targeting. At least one of countries, regions, or cities is required for ad set creation UNLESS savedAudienceId is provided (saved audience will provide targeting).'),
            cities: zod_1.z
                .array(zod_1.z.object({ key: zod_1.z.string() }))
                .nullable()
                .optional()
                .describe('City targeting. At least one of countries, regions, or cities is required for ad set creation UNLESS savedAudienceId is provided (saved audience will provide targeting).'),
        })
            .nullable()
            .optional()
            .describe('Geographic location targeting. REQUIRED if savedAudienceId is not provided: Must specify at least one of countries, regions, or cities. OPTIONAL if savedAudienceId is provided: Saved audience will provide targeting, but you can override specific fields.'),
        age_min: zod_1.z.number().min(18).max(65).nullable().optional().describe('Minimum age (18-65)'),
        age_max: zod_1.z
            .number()
            .min(18)
            .max(65)
            .nullable()
            .optional()
            .describe('Maximum age (18-65). When using Advantage+ audience (advantageAudience: true), age_max must be 65 or omitted. Facebook requires maximum age of 65 with Advantage+ to allow audience expansion. For stricter age targeting below 65, set advantageAudience to false.'),
        genders: zod_1.z
            .array(zod_1.z.number())
            .nullable()
            .optional()
            .describe('Gender targeting: [1] for male, [2] for female, [1, 2] for all genders'),
        interests: zod_1.z
            .array(zod_1.z.object({
            id: zod_1.z.string().describe('Interest ID from facebookAdsSearchTargeting tool'),
            name: zod_1.z
                .string()
                .nullable()
                .optional()
                .describe('Interest name for reference (optional)'),
        }))
            .nullable()
            .optional()
            .describe('Interest-based targeting. Use facebookAdsSearchTargeting with searchType "interests" to find interest IDs. Provide array of interest objects with id field. Example: [{"id": "6003139266461", "name": "Tennis"}]'),
        custom_audiences: zod_1.z
            .array(zod_1.z.object({
            id: zod_1.z
                .string()
                .describe('Custom audience or lookalike audience ID from facebookAdsListAudiences customAudiences array (NOT savedAudiences). These are customer lists, pixel audiences, app users, or lookalike audiences.'),
        }))
            .nullable()
            .optional()
            .describe('Custom and lookalike audience targeting. Use facebookAdsListAudiences to get audience IDs from the customAudiences array (NOT savedAudiences). These include customer lists, website visitors (pixel), app users, engagement audiences, and lookalike audiences. Provide array of audience objects with id field. Example: [{"id": "23851234567890"}]'),
        excluded_custom_audiences: zod_1.z
            .array(zod_1.z.object({
            id: zod_1.z
                .string()
                .describe('Custom audience or lookalike audience ID to exclude from facebookAdsListAudiences customAudiences array (NOT savedAudiences).'),
        }))
            .nullable()
            .optional()
            .describe('Exclude specific custom audiences or lookalike audiences. Use to exclude converters, existing customers, or specific segments from targeting. Get IDs from facebookAdsListAudiences customAudiences array (NOT savedAudiences).'),
        flexible_spec: zod_1.z
            .array(zod_1.z
            .object({
            interests: zod_1.z
                .array(zod_1.z.object({
                id: zod_1.z.string(),
                name: zod_1.z.string().nullable().optional(),
            }))
                .nullable()
                .optional(),
            behaviors: zod_1.z
                .array(zod_1.z.object({
                id: zod_1.z.string(),
                name: zod_1.z.string().nullable().optional(),
            }))
                .nullable()
                .optional(),
        })
            .describe('Each object in array represents OR logic between objects, AND logic within'))
            .nullable()
            .optional()
            .describe('Advanced targeting with OR/AND logic. Each array element is ORed together, fields within each element are ANDed. Use for complex targeting combinations. Example: [{"interests": [{"id": "1"}]}, {"behaviors": [{"id": "2"}]}] targets people with interest 1 OR behavior 2'),
        publisher_platforms: zod_1.z
            .array(zod_1.z.enum(['facebook', 'instagram', 'messenger', 'audience_network']))
            .nullable()
            .optional()
            .describe('Publisher platforms where ads will be shown. Options: facebook, instagram, messenger, audience_network. Example: ["facebook", "instagram"] to show ads on Facebook and Instagram only.'),
        facebook_positions: zod_1.z
            .array(zod_1.z.enum([
            'feed',
            'story',
            'marketplace',
            'video_feeds',
            'search',
            'right_column',
            'instant_article',
            'instream_video',
        ]))
            .nullable()
            .optional()
            .describe('Facebook ad positions where ads will appear. Options: feed, story, marketplace, video_feeds, search, right_column, instant_article, instream_video. Example: ["feed", "story"] to show ads in Facebook feed and stories only.'),
        instagram_positions: zod_1.z
            .array(zod_1.z.enum(['stream', 'story', 'reels']))
            .nullable()
            .optional()
            .describe('Instagram ad positions where ads will appear. Options: stream, story, reels. Example: ["stream", "story"] to show ads in Instagram feed and stories only.'),
        user_os: zod_1.z
            .array(zod_1.z.union([
            zod_1.z.enum(['iOS', 'Android']),
            zod_1.z
                .string()
                .regex(/^(iOS|Android)_ver_\d+\.\d+(_and_above|_to_\d+\.\d+)$/, 'Version strings must match pattern: iOS_ver_X.X_and_above or Android_ver_X.X_and_above'),
        ]))
            .nullable()
            .optional()
            .describe('Operating systems to target. Valid basic values: iOS, Android. You can also use version-specific strings like "iOS_ver_8.0_and_above", "Android_ver_4.2_and_above", or "iOS_ver_8.0_to_9.0" for more granular targeting. IMPORTANT: You cannot mix platforms with version-specific strings (e.g., ["Android", "iOS_ver_8.0_and_above"] is invalid). Valid combinations: ["iOS", "Android"], ["iOS"], ["Android_ver_4.2_and_above"]. Example: ["iOS", "Android"] to show ads on both platforms, or ["iOS_ver_12.0_and_above"] for iOS 12+. If omitted, ads will show on all operating systems.'),
        device_platforms: zod_1.z
            .array(zod_1.z.enum(['mobile', 'desktop', 'tablet']))
            .nullable()
            .optional()
            .describe('Device platforms to target. Options: mobile, desktop, tablet. Example: ["mobile"] to show ads only on mobile devices, or ["mobile", "tablet"] for mobile and tablet devices. Note: This is different from user_device which targets specific device models (e.g., "iPhone", "Samsung Galaxy").'),
        exclude_skippable_ads: zod_1.z
            .boolean()
            .nullable()
            .optional()
            .describe('Exclude skippable ads for in-stream video placements. When true, only non-skippable in-stream video ads will be shown. When false or omitted, both skippable and non-skippable in-stream video ads are allowed. Only applies when instream_video is included in facebook_positions.'),
        locales: zod_1.z
            .array(zod_1.z.number())
            .nullable()
            .optional()
            .describe('Language targeting by locale codes. Use facebookAdsSearchTargeting with searchType "locales" to find locale IDs. Example: [6] for English (All), [24] for Spanish (All). Restricts ads to users who use selected languages on Facebook. When omitted, ads show to all languages.'),
        advantageAudience: zod_1.z
            .boolean()
            .nullable()
            .optional()
            .describe("Enable Advantage+ Audience expansion. When true, Meta may expand beyond your specified age/gender/interests (while respecting hard constraints like geo, min age, language, excluded audiences). When false or omitted, Advantage+ expansion is disabled. The backend automatically converts this boolean to Meta's targeting_automation.advantage_audience flag and injects age/gender suggestion hints."),
    })
        .nullable()
        .optional()
        .describe('Audience targeting specification. REQUIRED if savedAudienceId is not provided: Must include geo_locations with at least one location (countries, regions, or cities). OPTIONAL if savedAudienceId is provided: Saved audience will provide targeting, but you can override specific fields. OPTIONAL: Add interests (from facebookAdsSearchTargeting), custom_audiences (from facebookAdsListAudiences), placements, device/OS settings, language filters (locales). When advantageAudience is true, Meta treats your detailed targeting (interests, custom audiences, lookalikes, age max, gender) as suggestions it can expand beyond, while still respecting hard constraints (location, minimum age, language, excluded audiences).'),
    promotedObject: promotedObjectSchema
        .nullable()
        .optional()
        .describe('Object to promote for pixel/page/app tracking. OPTIONAL for simple traffic campaigns (destination set in creative):\n' +
        '- OUTCOME_TRAFFIC: OPTIONAL - Only use for pixel tracking {"type": "pixel", "pixel_id": "123", "custom_event_type": "PAGE_VIEW"}\n' +
        '- OUTCOME_LEADS: REQUIRED - Use type "page" (for Facebook Lead Forms) OR type "pixel" (for website conversions)\n' +
        '- OUTCOME_SALES: REQUIRED - Format depends on conversionLocation:\n' +
        '  * Website: {"type": "pixel", "pixel_id": "...", "custom_event_type": "PURCHASE"}\n' +
        '  * App: {"type": "app", "application_id": "...", "object_store_url": "...", "custom_event_type": "PURCHASE"}\n' +
        '  * Message Destinations (MESSENGER/INSTAGRAM_DIRECT): {"type": "page", "page_id": "..."}\n' +
        '  * Calls: {"type": "page", "page_id": "..."}\n' +
        '- PAGE_LIKES: REQUIRED - Use type "page" (page ID from credentials by default)\n' +
        '- OUTCOME_AWARENESS/ENGAGEMENT: OPTIONAL - May be omitted entirely'),
    conversionLocation: zod_1.z
        .enum([
        // Traffic objective
        'WEBSITE',
        'APP',
        'MESSENGER',
        'WHATSAPP',
        'CALLS',
        'INSTAGRAM_PROFILE',
        // Engagement objective
        'ON_AD', // Video views, Post engagement, Event response
        'INSTAGRAM_DIRECT',
        'FACEBOOK_PAGE',
        // Leads objective
        'INSTANT_FORMS',
        'INSTAGRAM', // For leads
        // Sales / app
        'APP', // In-app / app promotion
        // Legacy/other
        'ON_POST',
        'ON_EVENT',
    ])
        .nullable()
        .optional()
        .describe('Conversion location - where conversions are expected to occur. This matches what you see in Facebook Ads Manager for single-intent destinations:\n' +
        '\n**Traffic (OUTCOME_TRAFFIC):**\n' +
        '- WEBSITE: Send people to your website\n' +
        '- APP: Send people to your app\n' +
        '- MESSENGER / WHATSAPP: Send people to messaging apps\n' +
        '- CALLS: Encourage phone calls\n' +
        '- INSTAGRAM_PROFILE: Send people to Instagram profile\n' +
        '\n**Engagement (OUTCOME_ENGAGEMENT):**\n' +
        '- ON_AD: Video views, Post engagement, Event response\n' +
        '- INSTAGRAM_DIRECT: Instagram Direct engagement\n' +
        '- FACEBOOK_PAGE: Page likes and engagement\n' +
        '- ON_POST / ON_EVENT: Post or event engagement on Facebook\n' +
        '\n**Leads (OUTCOME_LEADS):**\n' +
        '- INSTANT_FORMS: Lead forms on Facebook/Instagram (instant forms)\n' +
        '- WEBSITE: Website lead forms via pixel tracking\n' +
        '- INSTAGRAM_DIRECT: Instagram Chat Ads - leads via Instagram Direct messaging. Use for Instagram Chat Ads campaigns.\n' +
        '- MESSENGER: Leads via Facebook Messenger (for Facebook Messenger only, not Instagram)\n' +
        '- WHATSAPP: Leads via WhatsApp messaging\n' +
        '- INSTAGRAM: Instagram leads (general)\n' +
        '- CALLS: Phone call leads\n' +
        '\n**Sales / App Promotion:**\n' +
        '- WEBSITE: Website purchases via pixel tracking\n' +
        '- APP: In-app purchases or app promotion flows\n' +
        '- CALLS: Phone call destination (Meta destination_type PHONE_CALL) supported for OUTCOME_SALES when optimizing for conversions/value\n' +
        '\n**Instagram Chat Ads (Click to Instagram Direct):**\n' +
        'For Instagram Chat Ads, use `conversionLocation: "INSTAGRAM_DIRECT"`. Works with:\n' +
        '- `campaignObjective: "OUTCOME_LEADS"` + `optimizationGoal: "LEAD_GENERATION"` for lead collection via chat\n' +
        '- `campaignObjective: "OUTCOME_ENGAGEMENT"` + `optimizationGoal: "CONVERSATIONS"` for engagement\n' +
        'The ad creative should use `callToActionType: "INSTAGRAM_MESSAGE"`.\n' +
        '\n**Messenger Chat Ads:**\n' +
        'For Messenger Chat Ads, use `conversionLocation: "MESSENGER"`. The ad creative should use `callToActionType: "MESSAGE_PAGE"`.\n' +
        '\nMulti-intent locations like \"Website and instant forms\" are Ads Manager UI concepts and are not exposed directly here. If omitted, Meta will use default conversion locations based on campaign objective and optimization goal.'),
    startDate: zod_1.z
        .string()
        .nullish()
        .describe('Ad set start date as ISO 8601 date-time string. When specified, the ad set will not start running before this time. If omitted, the ad set starts immediately upon activation. Use this for scheduling campaigns to start at a specific date/time in the future. IMPORTANT: When startDate is specified, endDate must also be provided. Example: "2024-01-01T00:00:00Z" for Jan 1, 2024 midnight UTC.'),
    endDate: zod_1.z
        .string()
        .nullish()
        .describe('REQUIRED - Ad set end date as ISO 8601 date-time string. ALWAYS specify when creating ad sets to define when the campaign should stop. Must be at least 24 hours after the start date (or current time if no startDate). Use this for scheduling campaigns to end at a specific date/time. Example: "2025-01-31T00:00:00Z" for Jan 31, 2025 midnight UTC.'),
    pacingType: zod_1.z
        .array(zod_1.z.enum(['standard', 'day_parting', 'no_pacing']))
        .nullable()
        .optional()
        .describe('Budget pacing strategy. standard (default): Evenly distribute budget throughout the day. day_parting: Spend budget only during specific hours (requires schedule configuration). no_pacing: Spend budget as quickly as possible to maximize delivery. If omitted, Meta uses standard pacing. Note: This field accepts an array as per Meta API specification.'),
    savedAudienceId: zod_1.z
        .string()
        .nullable()
        .optional()
        .describe('Saved audience template ID from facebookAdsListAudiences savedAudiences array (NOT customAudiences). Saved audiences are targeting templates that include demographics, interests, behaviors, and other targeting criteria. When provided, the saved audience targeting spec will be applied and merged with any additional targeting parameters. Use this to reuse pre-configured targeting templates.'),
    attributionSpec: zod_1.z
        .array(zod_1.z.object({
        event_type: zod_1.z
            .enum(['CLICK_THROUGH', 'VIEW_THROUGH'])
            .describe('Attribution event type: CLICK_THROUGH or VIEW_THROUGH'),
        window_days: zod_1.z
            .number()
            .int()
            .min(1)
            .max(28)
            .describe('Attribution window in days (1-28). Common values: 1, 7 for clicks; 1 for views. Standard settings: 7-day click / 1-day view.'),
    }))
        .nullable()
        .optional()
        .describe('Attribution window settings for conversion tracking. Controls how long after ad interaction conversions are attributed. Standard lead gen setting: [{"event_type": "CLICK_THROUGH", "window_days": 7}, {"event_type": "VIEW_THROUGH", "window_days": 1}]. If omitted, Meta uses platform defaults (7-day click, 1-day view).'),
    dynamicCreative: zod_1.z
        .boolean()
        .nullable()
        .optional()
        .describe('Enable Dynamic Creative Optimization (DCO). When true, Facebook automatically tests different combinations of creative elements (headlines, descriptions, images, CTAs) to find the best performing combinations for each person. REQUIRED TRUE when providing multiple headlines/descriptions arrays in ad creatives for automatic A/B testing. REQUIRED FALSE or omitted for single creative element ads. Dynamic creative works best with 3-5 variations of each element (headlines, images, descriptions). Automatically generates up to 6,250 unique ad combinations from your creative assets. Only available for single image and video ads (not carousel, collection, or instant experience ads). Best for: Automated creative testing, scaling winning combinations, personalized ad delivery.'),
});
exports.facebookAdsCreateAdSetArgsSchema = exports.facebookAdsCreateAdSetArgsSchemaBase
    .refine(function (data) {
    // If lifetimeBudget is provided, require endDate
    if (data.lifetimeBudget && !data.dailyBudget) {
        return data.endDate != null;
    }
    return true;
}, {
    message: 'When using lifetimeBudget, you must provide endDate. Facebook requires an end date for all lifetime budget campaigns. Example: endDate: "2025-01-31T00:00:00Z" (Jan 31, 2025 midnight UTC)',
    path: ['endDate'],
})
    .refine(function (data) {
    // If startDate is specified, endDate must also be specified
    if (data.startDate != null) {
        return data.endDate != null;
    }
    return true;
}, {
    message: 'When startDate is specified, endDate must also be specified. Facebook requires an end date when a start date is provided.',
    path: ['endDate'],
});
exports.facebookAdsCreateAdSetResultSchema = zod_1.z.object({
    id: zod_1.z.string().describe('Ad set ID'),
    name: zod_1.z.string(),
    status: zod_1.z.string(),
    created: zod_1.z.boolean().default(true),
    adAccountId: zod_1.z.string().nullish().describe('Ad account ID used to create the ad set'),
});
// ---------- Update Ad Set ----------
exports.facebookAdsUpdateAdSetArgsSchemaBase = zod_1.z.object({
    adSetId: zod_1.z.string().describe('Ad set ID to update'),
    name: zod_1.z.string().nullable().optional().describe('New ad set name'),
    status: zod_1.z
        .enum(['ACTIVE', 'PAUSED', 'DELETED'])
        .nullable()
        .optional()
        .describe('New ad set status'),
    campaignHasBudget: zod_1.z
        .boolean()
        .default(true)
        .nullable()
        .optional()
        .describe('Indicates whether the campaign has its own budget set (CBO mode). When true, ad set budget parameters (dailyBudget/lifetimeBudget) will be discarded to prevent Meta API conflicts. When false, the campaign uses Ad Set Budget Optimization (ABO) and ad set budgets are required. This helps the AI agent make intentional budget decisions and prevents campaign-level vs ad set-level budget conflicts. If not provided, will auto-detect from campaign data.'),
    dailyBudget: zod_1.z
        .number()
        .nullable()
        .optional()
        .describe('New daily budget in account currency (cents). Minimum $30.00 (3000 cents).'),
    lifetimeBudget: zod_1.z
        .number()
        .nullable()
        .optional()
        .describe('New lifetime budget in account currency (cents). Minimum $30.00 (3000 cents).'),
    bidAmount: zod_1.z
        .number()
        .nullable()
        .optional()
        .describe('New bid amount in account currency (cents). Minimum $30.00 (3000 cents).'),
    bidStrategy: zod_1.z
        .enum([
        'LOWEST_COST_WITHOUT_CAP',
        'LOWEST_COST_WITH_BID_CAP',
        'COST_CAP',
        'LOWEST_COST_WITH_MIN_ROAS',
    ])
        .optional()
        .nullable()
        .describe('New bid strategy for the ad set. LOWEST_COST_WITHOUT_CAP: automatic bidding, LOWEST_COST_WITH_BID_CAP: maximum bid per auction, COST_CAP: target average cost, LOWEST_COST_WITH_MIN_ROAS: minimum ROAS constraint.'),
    bidConstraints: zod_1.z
        .object({
        roas_average_floor: zod_1.z
            .number()
            .nullable()
            .optional()
            .describe('Minimum ROAS target scaled by 10,000. Required when optimization goal is VALUE. Examples: 1.5 ROAS = 15000, 2.0 ROAS = 20000, 3.0 ROAS = 30000. Meta API requires values multiplied by 10,000.'),
    })
        .optional()
        .nullable()
        .describe('Bid constraints for ROAS optimization. Use with optimization goal VALUE.'),
    targeting: zod_1.z
        .object({
        geo_locations: zod_1.z
            .object({
            countries: zod_1.z
                .array(zod_1.z.string())
                .nullable()
                .optional()
                .describe('ISO 3166-1 alpha-2 country codes ONLY (e.g., ["US", "CA", "GB", "AU"]). Must be 2-letter codes, NOT country names. WRONG: ["United States", "Canada"]. CORRECT: ["US", "CA"].'),
            regions: zod_1.z
                .array(zod_1.z.object({ key: zod_1.z.string() }))
                .nullable()
                .optional()
                .describe('Region targeting. Update target regions for the ad set.'),
            cities: zod_1.z
                .array(zod_1.z.object({ key: zod_1.z.string() }))
                .nullable()
                .optional()
                .describe('City targeting. Update target cities for the ad set.'),
        })
            .nullable()
            .optional()
            .describe('Geographic location targeting. Update target regions, countries, or cities.'),
        age_min: zod_1.z.number().min(18).max(65).nullable().optional().describe('Minimum age (18-65)'),
        age_max: zod_1.z
            .number()
            .min(18)
            .max(65)
            .nullable()
            .optional()
            .describe('Maximum age (18-65). When using Advantage+ audience (advantageAudience: true), age_max must be 65 or omitted. Facebook requires maximum age of 65 with Advantage+ to allow audience expansion. For stricter age targeting below 65, set advantageAudience to false.'),
        genders: zod_1.z
            .array(zod_1.z.number())
            .nullable()
            .optional()
            .describe('Gender targeting: [1] for male, [2] for female, [1, 2] for all genders'),
        interests: zod_1.z
            .array(zod_1.z.object({
            id: zod_1.z.string().describe('Interest ID from facebookAdsSearchTargeting tool'),
            name: zod_1.z
                .string()
                .nullable()
                .optional()
                .describe('Interest name for reference (optional)'),
        }))
            .nullable()
            .optional()
            .describe('Interest-based targeting. Use facebookAdsSearchTargeting with searchType "interests" to find interest IDs. Provide array of interest objects with id field. Example: [{"id": "6003139266461", "name": "Tennis"}]'),
        custom_audiences: zod_1.z
            .array(zod_1.z.object({
            id: zod_1.z
                .string()
                .describe('Custom audience or lookalike audience ID from facebookAdsListAudiences customAudiences array (NOT savedAudiences). These are customer lists, pixel audiences, app users, or lookalike audiences.'),
        }))
            .nullable()
            .optional()
            .describe('Custom and lookalike audience targeting. Use facebookAdsListAudiences to get audience IDs from the customAudiences array (NOT savedAudiences). These include customer lists, website visitors (pixel), app users, engagement audiences, and lookalike audiences. Provide array of audience objects with id field. Example: [{"id": "23851234567890"}]'),
        excluded_custom_audiences: zod_1.z
            .array(zod_1.z.object({
            id: zod_1.z
                .string()
                .describe('Custom audience or lookalike audience ID to exclude from facebookAdsListAudiences customAudiences array (NOT savedAudiences).'),
        }))
            .nullable()
            .optional()
            .describe('Exclude specific custom audiences or lookalike audiences. Use to exclude converters, existing customers, or specific segments from targeting. Get IDs from facebookAdsListAudiences customAudiences array (NOT savedAudiences).'),
        flexible_spec: zod_1.z
            .array(zod_1.z
            .object({
            interests: zod_1.z
                .array(zod_1.z.object({
                id: zod_1.z.string(),
                name: zod_1.z.string().nullable().optional(),
            }))
                .nullable()
                .optional(),
            behaviors: zod_1.z
                .array(zod_1.z.object({
                id: zod_1.z.string(),
                name: zod_1.z.string().nullable().optional(),
            }))
                .nullable()
                .optional(),
        })
            .describe('Each object in array represents OR logic between objects, AND logic within'))
            .nullable()
            .optional()
            .describe('Advanced targeting with OR/AND logic. Each array element is ORed together, fields within each element are ANDed. Use for complex targeting combinations. Example: [{"interests": [{"id": "1"}]}, {"behaviors": [{"id": "2"}]}] targets people with interest 1 OR behavior 2'),
        publisher_platforms: zod_1.z
            .array(zod_1.z.enum(['facebook', 'instagram', 'messenger', 'audience_network']))
            .nullable()
            .optional()
            .describe('Publisher platforms where ads will be shown. Options: facebook, instagram, messenger, audience_network. Example: ["facebook", "instagram"] to show ads on Facebook and Instagram only.'),
        facebook_positions: zod_1.z
            .array(zod_1.z.enum([
            'feed',
            'story',
            'marketplace',
            'video_feeds',
            'search',
            'right_column',
            'instant_article',
            'instream_video',
        ]))
            .nullable()
            .optional()
            .describe('Facebook ad positions where ads will appear. Options: feed, story, marketplace, video_feeds, search, right_column, instant_article, instream_video. Example: ["feed", "story"] to show ads in Facebook feed and stories only.'),
        instagram_positions: zod_1.z
            .array(zod_1.z.enum(['stream', 'story', 'reels']))
            .nullable()
            .optional()
            .describe('Instagram ad positions where ads will appear. Options: stream, story, reels. Example: ["stream", "story"] to show ads in Instagram feed and stories only.'),
        user_os: zod_1.z
            .array(zod_1.z.union([
            zod_1.z.enum(['iOS', 'Android']),
            zod_1.z
                .string()
                .regex(/^(iOS|Android)_ver_\d+\.\d+(_and_above|_to_\d+\.\d+)$/, 'Version strings must match pattern: iOS_ver_X.X_and_above or Android_ver_X.X_and_above'),
        ]))
            .nullable()
            .optional()
            .describe('Operating systems to target. Valid basic values: iOS, Android. You can also use version-specific strings like "iOS_ver_8.0_and_above", "Android_ver_4.2_and_above", or "iOS_ver_8.0_to_9.0" for more granular targeting. IMPORTANT: You cannot mix platforms with version-specific strings (e.g., ["Android", "iOS_ver_8.0_and_above"] is invalid). Valid combinations: ["iOS", "Android"], ["iOS"], ["Android_ver_4.2_and_above"]. Example: ["iOS", "Android"] to show ads on both platforms, or ["iOS_ver_12.0_and_above"] for iOS 12+. If omitted, ads will show on all operating systems.'),
        device_platforms: zod_1.z
            .array(zod_1.z.enum(['mobile', 'desktop', 'tablet']))
            .nullable()
            .optional()
            .describe('Device platforms to target. Options: mobile, desktop, tablet. Example: ["mobile"] to show ads only on mobile devices, or ["mobile", "tablet"] for mobile and tablet devices. Note: This is different from user_device which targets specific device models (e.g., "iPhone", "Samsung Galaxy").'),
        exclude_skippable_ads: zod_1.z
            .boolean()
            .nullable()
            .optional()
            .describe('Exclude skippable ads for in-stream video placements. When true, only non-skippable in-stream video ads will be shown. When false or omitted, both skippable and non-skippable in-stream video ads are allowed. Only applies when instream_video is included in facebook_positions.'),
        locales: zod_1.z
            .array(zod_1.z.number())
            .nullable()
            .optional()
            .describe('Language targeting by locale codes. Use facebookAdsSearchTargeting with searchType "locales" to find locale IDs. Example: [6] for English (All), [24] for Spanish (All). Restricts ads to users who use selected languages on Facebook. When omitted, ads show to all languages.'),
        advantageAudience: zod_1.z
            .boolean()
            .nullable()
            .optional()
            .describe("Enable Advantage+ Audience expansion. When true, Meta may expand beyond your specified age/gender/interests (while respecting hard constraints like geo, min age, language, excluded audiences). When false or omitted, Advantage+ expansion is disabled. The backend automatically converts this boolean to Meta's targeting_automation.advantage_audience flag and injects age/gender suggestion hints."),
    })
        .optional()
        .nullable()
        .describe('Audience targeting specification. Update target regions, countries, cities, demographics, interests, custom audiences, placements, device/OS settings, language filters (locales), Advantage+ expansion (advantageAudience), or skippable ad preferences. All fields are optional for updates - only include fields you want to change.'),
    startDate: zod_1.z
        .string()
        .nullable()
        .optional()
        .describe('New ad set start date as ISO 8601 date-time string. Updates when the ad set will start running. If set to a future time, the ad set will not start before this time. IMPORTANT: When startDate is specified, endDate must also be provided. Example: "2024-01-01T00:00:00Z".'),
    endDate: zod_1.z
        .string()
        .nullable()
        .optional()
        .describe('New ad set end date as ISO 8601 date-time string. REQUIRED when startDate is specified. Updates when the ad set will stop running. Example: "2025-01-01T00:00:00Z".'),
    pacingType: zod_1.z
        .array(zod_1.z.enum(['standard', 'day_parting', 'no_pacing']))
        .nullable()
        .optional()
        .describe('Budget pacing strategy. standard: Evenly distribute budget throughout the day. day_parting: Spend budget only during specific hours. no_pacing: Spend budget as quickly as possible. Note: This field accepts an array as per Meta API specification.'),
    savedAudienceId: zod_1.z
        .string()
        .nullable()
        .optional()
        .describe('Saved audience template ID from facebookAdsListAudiences savedAudiences array (NOT customAudiences). Saved audiences are targeting templates that include demographics, interests, behaviors, and other targeting criteria. When provided, the saved audience targeting spec will be applied and merged with any additional targeting parameters. Use this to reuse pre-configured targeting templates.'),
    attributionSpec: zod_1.z
        .array(zod_1.z.object({
        event_type: zod_1.z
            .enum(['CLICK_THROUGH', 'VIEW_THROUGH'])
            .describe('Attribution event type: CLICK_THROUGH or VIEW_THROUGH'),
        window_days: zod_1.z
            .number()
            .int()
            .min(1)
            .max(28)
            .describe('Attribution window in days (1-28). Common values: 1, 7 for clicks; 1 for views.'),
    }))
        .nullable()
        .optional()
        .describe('New attribution window settings for conversion tracking. Standard lead gen: [{"event_type": "CLICK_THROUGH", "window_days": 7}, {"event_type": "VIEW_THROUGH", "window_days": 1}].'),
    dynamicCreative: zod_1.z
        .boolean()
        .nullable()
        .optional()
        .describe('Enable or disable Dynamic Creative Optimization (DCO). When true, Facebook automatically tests different combinations of creative elements (headlines, descriptions, images, CTAs) to find the best performing combinations. Set to true when updating to use multiple headlines/descriptions in ad creatives. Set to false to disable dynamic creative. Only available for single image and video ads (not carousel, collection, or instant experience ads).'),
});
exports.facebookAdsUpdateAdSetArgsSchema = exports.facebookAdsUpdateAdSetArgsSchemaBase
    .refine(function (data) {
    // If lifetimeBudget is being updated, require endDate
    if (data.lifetimeBudget && !data.dailyBudget) {
        return data.endDate != null;
    }
    return true;
}, {
    message: 'When updating to lifetimeBudget, you must provide endDate. Facebook requires an end date for all lifetime budget campaigns. Example: endDate: "2025-01-31T00:00:00Z" (Jan 31, 2025 midnight UTC)',
    path: ['endDate'],
})
    .refine(function (data) {
    // If startDate is specified, endDate must also be specified
    if (data.startDate != null) {
        return data.endDate != null;
    }
    return true;
}, {
    message: 'When startDate is specified, endDate must also be specified. Facebook requires an end date when a start date is provided.',
    path: ['endDate'],
});
exports.facebookAdsUpdateAdSetResultSchema = zod_1.z.object({
    id: zod_1.z.string().describe('Ad set ID'),
    updated: zod_1.z.boolean().default(true),
    success: zod_1.z.boolean(),
    adAccountId: zod_1.z.string().nullish().describe('Ad account ID used to update the ad set'),
});
// ==========================
// Compound Tool: facebookAdsAdSetManagement
// ==========================
exports.FacebookAdsAdSetAction = zod_1.z.enum(['createAdSet', 'updateAdSet']);
exports.facebookAdsAdSetManagementArgsSchema = zod_1.z.object({
    action: zod_1.z.discriminatedUnion('name', [
        zod_1.z.object({ name: zod_1.z.literal('createAdSet'), params: exports.facebookAdsCreateAdSetArgsSchema }),
        zod_1.z.object({ name: zod_1.z.literal('updateAdSet'), params: exports.facebookAdsUpdateAdSetArgsSchema }),
    ]),
    emitMode: zod_1.z.literal('single'),
});
var FacebookAdsAdSetManagementAllResultsSchema = zod_1.z.union([
    exports.facebookAdsCreateAdSetResultSchema,
    exports.facebookAdsUpdateAdSetResultSchema,
]);
var facebookAdsAdSetManagementResultSchemas = (0, common_1.defineDynamicSchemas)()({
    dependencies: ['action.name'],
    variants: [
        { when: { 'action.name': 'createAdSet' }, schema: exports.facebookAdsCreateAdSetResultSchema },
        { when: { 'action.name': 'updateAdSet' }, schema: exports.facebookAdsUpdateAdSetResultSchema },
    ],
    defaultSchema: FacebookAdsAdSetManagementAllResultsSchema,
});
exports.facebookAdsAdSetManagement = (0, common_1.createToolMetadata)({
    id: 'facebookAdsAdSetManagement',
    displayName: 'Facebook Ad Set Management',
    displayDescription: 'Create and update Facebook Ads ad sets',
    category: 'ads',
    color: common_1.BRAND_COLORS.META,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.META),
    icon: 'meta',
    argsSchema: exports.facebookAdsAdSetManagementArgsSchema,
}, facebookAdsAdSetManagementResultSchemas);
